/**
 * Language codes are stored; language NAMES are displayed.
 *
 * The backend persists a stable code — `EN`, `SW`, `KIK` on a voter, a free lowercase code
 * like `en` or `sw` on a survey instrument — and that is correct: a code survives a rename,
 * sorts stably and is what an export and an integration agree on. What is not correct is
 * showing it to a person. "SW" in a dropdown is a puzzle; "Kiswahili" is an answer, and a
 * mobilizer choosing a voter's preferred language should not have to know that KIS means
 * Kisii and KAL means Kalenjin.
 *
 * **Three of these codes collide with real ISO 639-1 codes for entirely different
 * languages** — `TUR` is Turkish, `KAL` is Greenlandic, `MAA` is a Maasai macrolanguage
 * whose ISO code is `mas`. So this map is consulted FIRST and `Intl.DisplayNames` only
 * after, never the other way round: a platform that renders a Turkana voter's language as
 * "Turkish" is worse than one that renders it as "TUR".
 */
const KENYA: Record<string, string> = {
  EN: 'English',
  SW: 'Kiswahili',
  KIK: 'Kikuyu',
  LUO: 'Dholuo (Luo)',
  KAM: 'Kamba',
  KAL: 'Kalenjin',
  LUY: 'Luhya',
  KIS: 'Kisii (Gusii)',
  MER: 'Meru',
  SOM: 'Somali',
  MAA: 'Maasai',
  TUR: 'Turkana',
  OTHER: 'Other',
}

/**
 * The set a voter's preferred language is chosen from, in the backend enum's own order —
 * which is roughly by number of speakers, and is deliberately not alphabetical: English and
 * Kiswahili are the two a mobilizer picks most and they belong at the top of the list, not
 * buried between Dholuo and Kalenjin.
 */
export const VOTER_LANGUAGES = [
  'EN', 'SW', 'KIK', 'LUO', 'KAM', 'KAL', 'LUY', 'KIS', 'MER', 'SOM', 'MAA', 'TUR', 'OTHER',
] as const

/** `{ EN: 'English', … }` — the shape a `<select>` over a label map wants. */
export const LANGUAGE_LABEL: Record<string, string> = KENYA

let display: Intl.DisplayNames | null | undefined

/**
 * A human name for a language code, whatever shape it arrives in.
 *
 * Case-insensitive, because the register stores `SW` and a survey instrument stores `sw`
 * and they are the same language. Unknown codes fall back to `Intl.DisplayNames` — which is
 * what makes a dossier item collected in French read "French" rather than "fr" — and then
 * to the code itself, upper-cased, which is still better than nothing.
 */
export function languageLabel(code: string | null | undefined): string {
  if (!code) return ''
  const key = code.trim()
  if (!key) return ''

  const known = KENYA[key.toUpperCase()]
  if (known) return known

  // Only ISO 639-1/2/3 shapes are worth asking about; a longer string is a made-up code and
  // Intl would either throw or hand back the input.
  if (/^[a-z]{2,3}(-[A-Za-z0-9]+)*$/i.test(key)) {
    if (display === undefined) {
      try {
        display = new Intl.DisplayNames(['en'], { type: 'language' })
      } catch {
        display = null   // ancient browser; the code itself is the fallback
      }
    }
    try {
      const name = display?.of(key.toLowerCase())
      if (name && name.toLowerCase() !== key.toLowerCase()) return name
    } catch {
      // An ill-formed tag throws rather than returning undefined. Fall through.
    }
  }
  return key.toUpperCase()
}

/** `Kiswahili (SW)` — for the places that must show both, like a translation picker. */
export function languageLabelWithCode(code: string | null | undefined): string {
  if (!code) return ''
  const label = languageLabel(code)
  return label.toUpperCase() === code.trim().toUpperCase()
    ? label
    : `${label} (${code.trim().toUpperCase()})`
}
